import { MapController } from './controller';
import type { DrawContext, MapStats, Orientation, Sector, Wash } from './controller';
import type { RenderEdge, RenderNode } from '@versesverse/core';

/*
 * THE DRAW DRIVER — a MapController plus its frame loop, with the scheduler and clock injected so the
 * SAME code runs on the main thread (requestAnimationFrame) and in the OffscreenCanvas worker
 * (setTimeout, because a worker has no rAF). It is DOM-free: it draws through a RenderTarget the caller
 * supplies — a window 2D context or a worker OffscreenCanvas 2D context, both of which satisfy it.
 *
 * The controller stays the brain (camera, pan/zoom, the fly tween, hit-testing, selection); this only
 * coalesces input into at most one draw per scheduled frame and keeps drawing while the tween runs.
 */

/** A 2D context this draws through: the controller's narrow surface plus the DPR transform the loop sets. */
export type RenderTarget = DrawContext & {
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
};

/** How the host schedules the next frame and reads the clock — rAF on a window, setTimeout in a worker. */
export interface EngineHost {
  now(): number;
  schedule(callback: () => void): void;
  /**
   * Called when the figure in front of the reader changes — never per frame.
   *
   * PUSHED, not polled. The chrome that names where the reader is has to be told, and it lives on
   * the main thread while the geometry lives here; polling it would be a round trip several times
   * a second for an answer that is usually the same one. This fires only on a change, so a reader
   * sitting still costs nothing at all.
   */
  onOrientation?(at: Orientation | null): void;
}

/**
 * How often the engine asks itself what the reader is looking at.
 *
 * `inFront` is O(n) over the figures, so it must not run per frame. A third of a second is under
 * the threshold where a caption feels laggy and far above the rate a pan would need it.
 */
const ORIENTATION_INTERVAL_MS = 320;

export class MapEngine {
  private readonly controller: MapController;
  private readonly ctx: RenderTarget;
  private readonly host: EngineHost;
  private readonly frame: () => void;
  private dpr = 1;
  private interacting = false;
  private scheduled = false;
  private destroyed = false;
  /** When the orientation was last worked out, and what it said — so only a change is reported. */
  private orientationAt = -Infinity;
  private orientationId: string | null = null;

  constructor(ctx: RenderTarget, nodes: RenderNode[], edges: RenderEdge[], host: EngineHost, washes: Wash[] = [], sectors: Sector[] = []) {
    this.ctx = ctx;
    this.host = host;
    this.controller = new MapController(nodes, edges, washes, sectors);
    this.frame = (): void => {
      this.scheduled = false;
      if (this.destroyed) return;
      /* Advance any fly-to tween first; while it runs we draw as `interacting` (no label measuring) and
         keep scheduling frames — the settling frame draws the labels back. */
      const animating = this.controller.advanceFocus(this.host.now());
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.controller.draw(this.ctx, { interacting: this.interacting || animating });
      this.reportOrientation();
      if (animating) this.requestDraw();
    };
  }

  /**
   * Tell the host what the reader is looking at, if it has changed since last time it was asked.
   *
   * Throttled AND compared: the throttle keeps an O(n) scan off most frames, and the comparison
   * keeps a message off the wire when the answer is the one already on screen. A drag across a
   * dense region changes it a few times; a drag across empty sky changes it once.
   */
  private reportOrientation(): void {
    const report = this.host.onOrientation;
    if (!report) return;
    const now = this.host.now();
    if (now - this.orientationAt < ORIENTATION_INTERVAL_MS) return;
    this.orientationAt = now;
    const at = this.controller.inFront();
    const id = at?.id ?? null;
    if (id === this.orientationId) return;
    this.orientationId = id;
    report(at);
  }

  private requestDraw(): void {
    if (this.scheduled || this.destroyed) return;
    this.scheduled = true;
    this.host.schedule(this.frame);
  }

  /** Set the CSS viewport and device-pixel ratio (the caller sizes the canvas bitmap to width×dpr). */
  setViewport(width: number, height: number, dpr: number): void {
    this.dpr = dpr;
    this.controller.setViewport(width, height);
    this.requestDraw();
  }

  resetCamera(): void { this.controller.resetCamera(); this.requestDraw(); }
  pan(dx: number, dy: number): void { this.controller.pan(dx, dy); this.requestDraw(); }
  zoomAt(sx: number, sy: number, factor: number): void { this.controller.zoomAt(sx, sy, factor); this.requestDraw(); }

  focusOn(id: string, options: { animate: boolean; inset: { right: number; bottom: number } }): void {
    if (this.controller.focusOn(id, { animate: options.animate, now: this.host.now(), inset: options.inset })) {
      this.requestDraw();
    }
  }

  /** The tour's camera — see `MapController.flyTo`. */
  flyTo(to: { x: number; y: number } | null, zoom: number, durMs: number): void {
    this.controller.flyTo(to, zoom, { now: this.host.now(), durMs });
    this.requestDraw();
  }

  setSelected(id: string | null): void { this.controller.setSelected(id); this.requestDraw(); }
  setInteracting(value: boolean): void { this.interacting = value; this.requestDraw(); }
  setChrome(bands: { x0: number; y0: number; x1: number; y1: number }[]): void { this.controller.setChrome(bands); this.requestDraw(); }
  /** What the reader has walked and what stands a step from it — the fog. Empty `walked` is no fog. */
  setFog(walked: readonly string[], within: readonly string[], mix: number): void {
    this.controller.setFog(new Set(walked), new Set(within), mix);
    this.requestDraw();
  }

  hitTest(sx: number, sy: number): string | null { return this.controller.hitTest(sx, sy); }
  stats(): MapStats { return this.controller.snapshot(); }

  destroy(): void { this.destroyed = true; }
}
